import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useRestaurants } from '../../hooks/useRestaurants';
import { api, API_URL } from '../../utils/apiClient';
import AchievementModal from './AchievementModal';
import './AchievementsPanel.css';

interface AchievementCondition {
  id: string;
  conditionType: string;
  conditionValue: number;
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string | null;
  points: number;
  restaurantId: string | null;
  conditionRules: AchievementCondition[];
  restaurant?: { id: string; name: string; slug: string } | null;
  _count?: { users: number };
}

interface BadgeTier {
  id: string;
  name: string;
  threshold: number;
  imageUrl: string | null;
}

function AddTierForm({ badgeId, onAdded, token }: { badgeId: string; onAdded: () => void; token: string }) {
  const [name, setName] = React.useState('');
  const [threshold, setThreshold] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !threshold) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/badges/${badgeId}/tiers`,
        { name, threshold: Number(threshold), imageUrl: imageUrl || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setName(''); setThreshold(''); setImageUrl('');
      onAdded();
    } catch (err: unknown) {
      alert((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
      <input placeholder="Название тира" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 120, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 6 }} />
      <input type="number" placeholder="Порог (посещений)" value={threshold} onChange={(e) => setThreshold(e.target.value)} style={{ width: 140, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 6 }} />
      <input placeholder="URL изображения (необязательно)" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} style={{ flex: 2, minWidth: 200, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 6 }} />
      <button type="submit" disabled={saving} style={{ padding: '4px 12px', background: 'var(--a-amber)', color: 'var(--a-white)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
        {saving ? '...' : 'Добавить тир'}
      </button>
    </form>
  );
}

function AchievementsPanel() {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const { restaurants, fetchRestaurants } = useRestaurants();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);

  const [expandedBadgeTiers, setExpandedBadgeTiers] = useState<string | null>(null);
  const [badgeTiers, setBadgeTiers] = useState<Record<string, BadgeTier[]>>({});

  const isRestaurantOwner = user?.role === 'RESTAURANT_OWNER';
  const effectiveRestaurantId = isRestaurantOwner ? user?.restaurantId : selectedRestaurantId;

  useEffect(() => {
    if (isRestaurantOwner && user?.restaurantId) {
      setSelectedRestaurantId(user.restaurantId);
    } else if (!isRestaurantOwner && selectedRestaurantId === '') {
      // Developer/Super Admin: default to Global so they can add platform achievements
      setSelectedRestaurantId('__global__');
    }
  }, [user, restaurants, isRestaurantOwner, selectedRestaurantId]);

  const fetchAchievements = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<Achievement[]>('/api/achievements');
      setAchievements(data);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'achievements.failedLoad');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAchievements();
    if (!isRestaurantOwner) fetchRestaurants();
  }, [fetchAchievements, fetchRestaurants, isRestaurantOwner]);

  const handleCreate = () => {
    setEditingAchievement(null);
    setShowModal(true);
  };

  const handleEdit = (achievement: Achievement) => {
    setEditingAchievement(achievement);
    setShowModal(true);
  };

  const handleDelete = async (achievement: Achievement) => {
    if (!confirm(`Delete "${achievement.name}"? ${t('achievements.confirmDelete')}`)) return;
    try {
      await api.delete(`/api/achievements/${achievement.id}`);
      fetchAchievements();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'achievements.failedDelete');
    }
  };

  const fetchTiers = useCallback(async (badgeId: string) => {
    try {
      const { data } = await api.get<BadgeTier[]>(`/api/badges/${badgeId}/tiers`);
      setBadgeTiers((prev) => ({ ...prev, [badgeId]: data }));
    } catch {
      setBadgeTiers((prev) => ({ ...prev, [badgeId]: [] }));
    }
  }, []);

  const handleDeleteTier = async (badgeId: string, tierId: string) => {
    if (!confirm('Удалить тир?')) return;
    try {
      await api.delete(`/api/badges/tiers/${tierId}`);
      fetchTiers(badgeId);
    } catch (err: unknown) {
      alert((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Ошибка');
    }
  };

  const isGlobal = selectedRestaurantId === '__global__';
  const filteredAchievements = isGlobal
    ? achievements.filter((a) => a.restaurantId === null)
    : effectiveRestaurantId
      ? achievements.filter(
          (a) =>
            a.restaurantId === effectiveRestaurantId ||
            a.restaurantId === null // include global when viewing a restaurant
        )
      : achievements;

  const canCreate = isRestaurantOwner
    ? !!user?.restaurantId
    : !!selectedRestaurantId || isGlobal;

  // Badges section: only show for restaurant context (badges are restaurant-scoped)
  const badgeRestaurantId = isRestaurantOwner ? user?.restaurantId : (isGlobal ? null : effectiveRestaurantId || null);

  const [badges, setBadges] = useState<Array<{ id: string; name: string; description: string; icon: string | null; rarity: string }>>([]);
  const [badgesLoading, setBadgesLoading] = useState(false);

  const fetchBadges = useCallback(async () => {
    if (!badgeRestaurantId) return;
    setBadgesLoading(true);
    try {
      const { data } = await api.get<typeof badges>('/api/badges');
      setBadges(data);
    } catch {
      setBadges([]);
    } finally {
      setBadgesLoading(false);
    }
  }, [badgeRestaurantId]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  return (
    <div className="menu-panel achievements-panel">
      <div className="panel-card">
        <div className="panel-controls">
          <div className="control-group">
            {!isRestaurantOwner && (
              <select
                value={selectedRestaurantId}
                onChange={(e) => setSelectedRestaurantId(e.target.value)}
                className="restaurant-select"
              >
                <option value="">{t('achievements.allRestaurants')}</option>
                <option value="__global__">Global (platform)</option>
                {restaurants.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            )}
            <motion.button
              onClick={handleCreate}
              className="btn-secondary"
              disabled={!canCreate}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              + {t('achievements.addAchievement')}
            </motion.button>
          </div>
          {isRestaurantOwner && user?.restaurant && (
            <div className="restaurant-info">
              <strong>{user.restaurant.name}</strong> • {filteredAchievements.length} {t('achievements.achievementsCount')}
            </div>
          )}
        </div>

        {error && <div className="error-box">{error.startsWith('achievements.') ? t(error) : error}</div>}

        {loading ? (
          <div className="loading-state">{t('achievements.loading')}</div>
        ) : filteredAchievements.length === 0 ? (
          <div className="empty-state">
            {canCreate
              ? t('achievements.noAchievementsYet')
              : t('achievements.selectOrCreate')}
          </div>
        ) : (
          <div className="achievements-grid">
            {filteredAchievements.map((achievement) => (
              <div key={achievement.id} className="achievement-card">
                <div className="achievement-icon">
                  {achievement.icon?.startsWith('http') ? (
                    <img src={achievement.icon} alt="" />
                  ) : (
                    <span>{achievement.icon || ''}</span>
                  )}
                </div>
                <div className="achievement-content">
                  <h3 className="achievement-name">{achievement.name}</h3>
                  <p className="achievement-desc">{achievement.description}</p>
                  <div className="achievement-meta">
                    <span className="achievement-points">{achievement.points} {t('achievements.pts')}</span>
                    {achievement.conditionRules?.[0] && (
                      <span className="achievement-condition">
                        {achievement.conditionRules[0].conditionType === 'totalOrders'
                          ? `${achievement.conditionRules[0].conditionValue} ${t('achievements.orders')}`
                          : `₸${achievement.conditionRules[0].conditionValue} ${t('achievements.spent')}`}
                      </span>
                    )}
                    {achievement._count?.users !== undefined && (
                      <span className="achievement-unlocks">
                        {achievement._count.users} {t('achievements.unlocked')}
                      </span>
                    )}
                  </div>
                  <div className="achievement-actions">
                    {(user?.role === 'SUPER_ADMIN' ||
                      user?.role === 'DEVELOPER' ||
                      achievement.restaurantId === user?.restaurantId) && (
                      <>
                        <button
                          type="button"
                          className="btn-sm"
                          onClick={() => handleEdit(achievement)}
                        >
                          {t('achievements.edit')}
                        </button>
                        <button
                          type="button"
                          className="btn-sm btn-danger"
                          onClick={() => handleDelete(achievement)}
                        >
                          {t('achievements.delete')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── BADGES SECTION ── */}
        {badgeRestaurantId && (
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Бейджи</h3>
            {badgesLoading ? (
              <div className="loading-state">Загрузка бейджей...</div>
            ) : badges.length === 0 ? (
              <div className="empty-state">Нет бейджей для этого ресторана</div>
            ) : (
              <div className="achievements-grid">
                {badges.map((badge) => (
                  <div key={badge.id} className="achievement-card">
                    <div className="achievement-icon">
                      {badge.icon?.startsWith('http') ? (
                        <img src={badge.icon} alt="" />
                      ) : (
                        <span>{badge.icon || ''}</span>
                      )}
                    </div>
                    <div className="achievement-content">
                      <h3 className="achievement-name">{badge.name}</h3>
                      <p className="achievement-desc">{badge.description}</p>
                      <div className="achievement-meta">
                        <span className="achievement-condition">{badge.rarity}</span>
                      </div>
                      <div className="achievement-actions">
                        <button
                          type="button"
                          className="btn-sm"
                          onClick={() => {
                            if (expandedBadgeTiers === badge.id) {
                              setExpandedBadgeTiers(null);
                            } else {
                              setExpandedBadgeTiers(badge.id);
                              fetchTiers(badge.id);
                            }
                          }}
                        >
                          {expandedBadgeTiers === badge.id ? 'Скрыть тиры' : 'Тиры'}
                        </button>
                      </div>

                      {expandedBadgeTiers === badge.id && (
                        <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
                          {(badgeTiers[badge.id] ?? []).length === 0 ? (
                            <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>Нет тиров</div>
                          ) : (
                            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                              {(badgeTiers[badge.id] ?? []).map((tier) => (
                                <li key={tier.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
                                  {tier.imageUrl && (
                                    <img src={tier.imageUrl} alt={tier.name} style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} />
                                  )}
                                  <span style={{ flex: 1 }}><strong>{tier.name}</strong> — {tier.threshold} посещений</span>
                                  <button
                                    type="button"
                                    className="btn-sm btn-danger"
                                    onClick={() => handleDeleteTier(badge.id, tier.id)}
                                    style={{ fontSize: 11, padding: '2px 6px' }}
                                  >
                                    
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {token && (
                            <AddTierForm
                              badgeId={badge.id}
                              token={token}
                              onAdded={() => fetchTiers(badge.id)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <AchievementModal
          restaurantId={isGlobal ? null : (effectiveRestaurantId || null)}
          achievement={editingAchievement}
          onClose={() => {
            setShowModal(false);
            setEditingAchievement(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingAchievement(null);
            fetchAchievements();
          }}
        />
      )}
    </div>
  );
}

export default AchievementsPanel;
